// CaptureControls — 截圖 / 錄影按鈕列（Live 與回放共用，APP#17）。
//
// 包了 useMediaCapture：截圖按鈕（無畫面時 disabled）、錄影開關，錄影中顯示
// 紅點 + 計時器(mm:ss)。錄影狀態以 aria-live 播報；按鈕有 aria-label。
// 不支援錄影的瀏覽器隱藏錄影鈕並顯示提示。

import { useTranslation } from 'react-i18next';
import { useMediaCapture } from './useMediaCapture';
import { formatElapsed } from './captureLib';
import './capture.css';

interface Props {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** WebRTC 串流（優先錄）；回放頁無串流傳 null → 走 video.captureStream()。 */
  mediaStream?: MediaStream | null;
  deviceName: string;
  /** 選填：來源裝置 id（存入本地相簿供裝置層過濾，APP#42）。 */
  deviceId?: string;
  /** 來源是否就緒（live=streaming / 回放=有 src）；false 時截圖/錄影皆 disabled。 */
  ready: boolean;
  className?: string;
}

export function CaptureControls({ videoRef, mediaStream = null, deviceName, deviceId, ready, className }: Props) {
  const { t } = useTranslation();
  const cap = useMediaCapture(videoRef, mediaStream, deviceName, deviceId);

  const failureMsg = (() => {
    switch (cap.lastError) {
      case 'no-frame':
        return t('capture.errNoFrame');
      case 'unsupported':
        return t('capture.errUnsupported');
      case 'quota':
        return t('capture.errQuota');
      case 'encode':
      case 'start-failed':
        return t('capture.errGeneric');
      default:
        return null;
    }
  })();

  return (
    <div className={`cap-controls${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className="btn-ghost cap-btn"
        disabled={!ready}
        onClick={() => void cap.screenshot()}
        aria-label={t('capture.screenshotAria')}
        data-testid="cap-screenshot"
      >
        <span aria-hidden="true">📷</span> {t('capture.screenshot')}
      </button>

      {cap.canRecord ? (
        cap.recording ? (
          <button
            type="button"
            className="btn-primary cap-btn cap-recording"
            onClick={() => cap.stopRecording()}
            aria-label={t('capture.stopRecordingAria')}
            data-testid="cap-record-stop"
          >
            <span className="cap-rec-dot" aria-hidden="true" />
            {t('capture.stopRecording')} <span className="cap-timer">{formatElapsed(cap.elapsed)}</span>
          </button>
        ) : (
          <button
            type="button"
            className="btn-ghost cap-btn"
            disabled={!ready}
            onClick={() => cap.startRecording()}
            aria-label={t('capture.recordAria')}
            data-testid="cap-record-start"
          >
            <span aria-hidden="true">⏺</span> {t('capture.record')}
          </button>
        )
      ) : null}

      {/* 錄影狀態 / 失敗提示：螢幕報讀（a11y） */}
      <span className="cap-status" role="status" aria-live="polite">
        {cap.recording
          ? t('capture.recordingStatus', { time: formatElapsed(cap.elapsed) })
          : failureMsg ?? ''}
      </span>
    </div>
  );
}
